'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { cn } from './cn.js';
import { themeMeta, readCssVar, type McTheme } from './tokens.js';

export type { McTheme };

export const MC_THEME_STORAGE_KEY = 'mc-theme';

/** Tema aplicado na primeira visita (sem preferência guardada). */
export const MC_DEFAULT_THEME: McTheme = 'light';

/**
 * Script inline para injectar no `<head>` antes da pintura — evita flash do
 * tema errado ao recarregar a página.
 */
export const themeInitScript = `(function(){try{var k='${MC_THEME_STORAGE_KEY}';var t=localStorage.getItem(k);if(t!=='light'&&t!=='dark')t='${MC_DEFAULT_THEME}';document.documentElement.dataset.theme=t;document.documentElement.style.colorScheme=t==='light'?'light':'dark';var m=document.querySelector('meta[name=theme-color]');if(m)m.content=t==='light'?'#F4F9F7':'#050F1C';}catch(e){}})();`;

export function getStoredTheme(): McTheme {
  if (typeof window === 'undefined') return MC_DEFAULT_THEME;
  try {
    const t = localStorage.getItem(MC_THEME_STORAGE_KEY);
    if (t === 'light' || t === 'dark') return t;
  } catch {
    /* modo privado */
  }
  return MC_DEFAULT_THEME;
}

export function applyTheme(theme: McTheme): void {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = themeMeta[theme].colorScheme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', themeMeta[theme].themeColor);
}

interface ThemeContextValue {
  theme: McTheme;
  setTheme: (theme: McTheme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  children,
  defaultTheme = MC_DEFAULT_THEME,
}: {
  children: ReactNode;
  defaultTheme?: McTheme;
}): ReactNode {
  const [theme, setThemeState] = useState<McTheme>(() => {
    if (typeof window === 'undefined') return defaultTheme;
    return getStoredTheme();
  });

  const setTheme = useCallback((next: McTheme) => {
    setThemeState(next);
    applyTheme(next);
    try {
      localStorage.setItem(MC_THEME_STORAGE_KEY, next);
    } catch {
      /* modo privado */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [setTheme, theme]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme deve ser usado dentro de ThemeProvider');
  }
  return ctx;
}

/** Cores para gráficos Recharts — reactivam ao mudar de tema. */
export function useChartColors(): {
  grid: string;
  cursor: string;
  tooltipBg: string;
  tooltipBorder: string;
  axis: string;
  accent: string;
  danger: string;
  ink: string;
  warning: string;
  depth: string;
} {
  const { theme } = useTheme();
  return useMemo(
    () => ({
      grid: readCssVar('--mc-chart-grid', 'rgba(232,246,238,0.08)'),
      cursor: readCssVar('--mc-chart-cursor', 'rgba(232,246,238,0.04)'),
      tooltipBg: readCssVar('--mc-tooltip-bg', 'rgba(11,30,51,0.95)'),
      tooltipBorder: readCssVar('--mc-tooltip-border', 'rgba(232,246,238,0.18)'),
      axis: readCssVar('--mc-ink-3', '#7E9A8F'),
      accent: readCssVar('--mc-accent', '#2FE07A'),
      danger: readCssVar('--mc-danger', '#E5484D'),
      ink: readCssVar('--mc-ink', '#E8F6EE'),
      warning: readCssVar('--mc-warning', '#E0A64C'),
      depth: readCssVar('--mc-depth', '#1B75BC'),
    }),
    [theme],
  );
}

export function tooltipContentStyle(chart: ReturnType<typeof useChartColors>): CSSProperties {
  return {
    background: chart.tooltipBg,
    border: `1px solid ${chart.tooltipBorder}`,
    borderRadius: 12,
    color: chart.ink,
    fontSize: 12,
  };
}

function IconSun({ className }: { className?: string }): ReactNode {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <circle cx="10" cy="10" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M10 2v2M10 16v2M3.5 10H2M18 10h-1.5M5.05 5.05l1.06 1.06M13.9 13.9l1.06 1.06M5.05 14.95l1.06-1.06M13.9 6.1l1.06-1.06"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconMoon({ className }: { className?: string }): ReactNode {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <path
        d="M15.5 11.2a6.3 6.3 0 01-8.7-8.7 6.5 6.5 0 109.7 9.7 6.2 6.2 0 01-1 0z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md';
}

/**
 * Botão compacto sol/lua — alterna entre tema claro e escuro.
 * O tema escuro permanece intacto; o claro usa `[data-theme="light"]`.
 */
export function ThemeToggle({ className, size = 'md' }: ThemeToggleProps): ReactNode {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';
  const dim = size === 'sm' ? 'size-9' : 'size-11';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={cn(
        'mc-theme-toggle grid place-items-center rounded-mc-pill border border-mc-line2',
        'text-mc-ink2 transition-colors hover:border-mc-accent/50 hover:bg-mc-surface-hover hover:text-mc-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mc-accent focus-visible:ring-offset-2 focus-visible:ring-offset-mc-bg',
        dim,
        className,
      )}
      aria-label={isLight ? 'Ativar tema escuro' : 'Ativar tema claro'}
      title={isLight ? 'Tema escuro' : 'Tema claro'}
    >
      {isLight ? <IconMoon className="size-5" /> : <IconSun className="size-5" />}
    </button>
  );
}
